import { defineStore } from 'pinia';

import { useCompaniesStore } from '@/features/Companies/store';
import { useFeaturesStore } from '@/features/Features';
import { buildFeatureObject } from '@/features/Global/searchDataUtils';
import type { SearchData, Sparte } from '@/features/Global/types';
import { SEARCH_DATA_CONTEXT, SPARTEN_STATUS } from '@/features/Global/types';
import { useProfileStore } from '@/features/Profile';
import type { WEBREPORT_TYPE } from '@/features/Sparten/types';
import { useTariffStore } from '@/features/Tariffs/store';
import { useApi } from '@/services/api/useApi';
import { VGL_ERROR_CODE } from '@/services/errorHandler/errorCodes';
import { getErrorMessage } from '@/services/errorHandler/utils';
import { VglError } from '@/services/errorHandler/vglError';
import { getCurrentSpartenConfig } from '@/utils/getCurrentSpartenConfig/getCurrentSpartenConfig';
import hashCode from '@/utils/hashCode/hashCode';
import { useSimpleChoicesStore } from '@/features/ChoicesLists/Simple/store';
import { getBlacklistedCompaniesHash } from '@/features/Companies/utils';
import { useEntityManager } from '@/features/EntityManager/useEntityManager';
import { ENTITY_TYPE } from '@/features/EntityManager/types';

interface GlobalStore {
  sparte: Sparte | null;
  spartenStatus: SPARTEN_STATUS | null;
  isWebreport: boolean;
  isVersicherungsangabenContext: boolean;
  webreportType: WEBREPORT_TYPE | null;
}

export const useGlobalStore = defineStore('GlobalStore', {
  state: (): GlobalStore => ({
    sparte: null,
    spartenStatus: null,
    isWebreport: false,
    isVersicherungsangabenContext: false,
    webreportType: null,
  }),

  actions: {
    async loadSpartenStatus() {
      try {
        const globalApi = useApi().global;
        const [, resultPromise] = globalApi.loadSpartenStatus();
        const result = await resultPromise;

        if (result === null) {
          this.spartenStatus = SPARTEN_STATUS.ERROR;
        } else {
          this.spartenStatus = result[this.getSparte];
        }
      } catch (error) {
        this.spartenStatus = SPARTEN_STATUS.ERROR;

        throw new VglError(VGL_ERROR_CODE.GLOBAL_LOADING_FAILED, {
          additionalErrorMessage: `Error: ${getErrorMessage(error)} - Sparte: ${
            this.getSparte
          }`,
          cause: error,
        });
      }
    },

    async initApp() {
      const setupPromises = [
        this.loadSpartenStatus(),
        useSimpleChoicesStore().preloadSpartenChoices(),
        useFeaturesStore().loadFeatures(),
        useProfileStore().loadProfiles(),
      ];

      await Promise.all(setupPromises).catch((error) => {
        throw new VglError(VGL_ERROR_CODE.GLOBAL_INITIALIZATION_FAILED, {
          additionalErrorMessage: `Error: ${getErrorMessage(error)}`,
          cause: error,
        });
      });
    },

    getTariffSearchData(dehydrate = false) {
      const searchData: SearchData = {
        eingabedaten: {},
        entities: {},
        merkmale: [],
        blacklistedCompaniesHash: getBlacklistedCompaniesHash(),
      };

      if (this.sparte !== null) {
        const spartenConfig = getCurrentSpartenConfig();
        searchData.eingabedaten = spartenConfig.buildInputDataObject(
          SEARCH_DATA_CONTEXT.TARIFF,
          dehydrate,
        );
        searchData.entities =
          useEntityManager().prepareEntitiesForBackendRequest([
            ENTITY_TYPE.CLIENT,
          ]);
        searchData.merkmale = buildFeatureObject();

        const allTimeTariffsTransformatorCallback =
          spartenConfig.allTimeTariffTransformationCallback;
        if (allTimeTariffsTransformatorCallback !== undefined) {
          searchData.allTimeTariff = allTimeTariffsTransformatorCallback();
        }
      }

      return Object.freeze(searchData);
    },

    getTariffSearchHash(): string {
      const searchData: Partial<SearchData> = {
        ...this.getTariffSearchData(true),
      };
      delete searchData.entities;

      return hashCode({
        searchData,
        calculationRelevantEntities:
          useEntityManager().prepareCalculationRelevantEntityData(),
        companies: useCompaniesStore().companiesSelection,
        isDirectAgreement: useTariffStore().filters.directAgreement,
        blacklistedCompanies: useCompaniesStore().blacklistedList,
      });
    },
  },

  getters: {
    getSparte(state) {
      const { sparte } = state;
      if (sparte) {
        return sparte;
      }

      throw new VglError(VGL_ERROR_CODE.GLOBAL_NO_SPARTE);
    },

    getQuoteSearchData(state) {
      const searchData: SearchData = {
        eingabedaten: {},
        entities: {},
        merkmale: [],
        blacklistedCompaniesHash: getBlacklistedCompaniesHash(),
      };

      if (state.sparte !== null) {
        const spartenConfig = getCurrentSpartenConfig();
        searchData.eingabedaten = spartenConfig.buildInputDataObject(
          SEARCH_DATA_CONTEXT.OFFER,
        );
        searchData.entities =
          useEntityManager().prepareEntitiesForBackendRequest([
            ENTITY_TYPE.CLIENT,
          ]);
        searchData.merkmale = buildFeatureObject();

        const allTimeTariffsTransformatorCallback =
          spartenConfig.allTimeTariffTransformationCallback;
        if (allTimeTariffsTransformatorCallback !== undefined) {
          searchData.allTimeTariff = allTimeTariffsTransformatorCallback(true);
        }
      }

      return Object.freeze(searchData);
    },

    getQuoteSearchHash() {
      return hashCode(this.getQuoteSearchData);
    },

    isKFZSparte(state) {
      const kfzSparten: Sparte[] = [
        'pkw',
        'pkwv2',
        'nutz',
        'nutzv2',
        'krad',
        'kradv2',
        'camp',
        'campv2',
      ];

      return state.sparte !== null && kfzSparten.includes(state.sparte);
    },
  },
});
